import React, { useState, useEffect, useCallback, useRef } from 'react';
import './DataViewerPanel.css';

/**
 * DataViewerPanel – an in-app spreadsheet-style viewer that shows the
 * proposal-level data slice matching the current filters, connection-strength
 * slider, and node selection.  Supports sorting, full-text search, pagination,
 * and column visibility toggles.
 */
const DataViewerPanel = ({
  filters,
  visibleNodeIds,
  minLinkWeight,
  selectedNodeIds,
  neighborNodeIds,
  authFetch,
  apiBaseUrl,
  themeColors,
  theme,
  onHelpSection,
  onOpenDocument,
  cellScope,             // optional {yAxis,yValue,xAxis,xValue} — restrict to one heatmap cell
}) => {
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const colMenuRef = useRef(null);
  const fetchIdRef = useRef(0); // race-condition guard

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 when filters/search/sort change
  useEffect(() => {
    setPage(1);
  }, [filters, debouncedSearch, sortColumn, sortDirection, visibleNodeIds, selectedNodeIds, minLinkWeight, cellScope]);

  // Fetch data from backend
  const fetchData = useCallback(async () => {
    const id = ++fetchIdRef.current;
    setLoading(true);
    try {
      const hasSelection = selectedNodeIds && selectedNodeIds.size > 0;
      const body = {
        filters,
        page,
        pageSize,
        sortColumn,
        sortDirection,
        search: debouncedSearch,
        ...(visibleNodeIds ? { visibleNodeIds: Array.from(visibleNodeIds) } : {}),
        minLinkWeight,
        ...(cellScope ? { cellScope: { yAxis: cellScope.yAxis, yValue: cellScope.yValue, xAxis: cellScope.xAxis, xValue: cellScope.xValue } } : {}),
        ...(hasSelection ? {
          selectedNodeIds: Array.from(selectedNodeIds),
          selectedNeighborIds: neighborNodeIds ? [...neighborNodeIds] : []
        } : {})
      };

      const response = await authFetch(`${apiBaseUrl}/api/data-viewer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (id !== fetchIdRef.current) return; // stale response

      if (response.ok) {
        const data = await response.json();
        setRows(data.rows || []);
        setColumns(data.columns || []);
        setTotalRows(data.totalRows || 0);
        setTotalPages(data.totalPages || 1);
      }
    } catch (err) {
      console.error('DataViewer fetch error:', err);
    } finally {
      if (id === fetchIdRef.current) setLoading(false);
    }
  }, [filters, page, pageSize, sortColumn, sortDirection, debouncedSearch,
      visibleNodeIds, minLinkWeight, selectedNodeIds, neighborNodeIds, authFetch, apiBaseUrl, cellScope]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Close column menu when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (colMenuRef.current && !colMenuRef.current.contains(e.target)) {
        setColMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Column sort handler
  const handleSort = (colKey) => {
    if (sortColumn === colKey) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(colKey);
      setSortDirection('asc');
    }
  };

  // Toggle column visibility
  const toggleColumn = (colKey) => {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      if (next.has(colKey)) next.delete(colKey);
      else next.add(colKey);
      return next;
    });
  };

  const visibleColumns = columns.filter(c => !hiddenColumns.has(c.key));

  // Extract a filename for the row if one of the columns looks like a source
  // text path / filename. Lets the viewer trigger be generic across tenants.
  const getRowFilename = (row) => {
    if (!row) return null;
    const candidates = ['filename', 'text_path', 'file', 'doc_filename'];
    for (const k of candidates) {
      if (row[k]) {
        const v = String(row[k]);
        // Strip directory if present.
        const slash = v.lastIndexOf('/');
        return slash >= 0 ? v.slice(slash + 1) : v;
      }
    }
    return null;
  };

  const getRowParaIdx = (row) => {
    if (!row) return null;
    const v = row.para_idx ?? row.paraIdx ?? null;
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  // Format cell value for display
  const formatCell = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  return (
    <div className={`data-viewer-panel theme-${theme}`}>
      {/* Toolbar row */}
      <div className="dv-toolbar">
        <div className="dv-search-container">
          <input
            type="text"
            className="dv-search-input"
            placeholder="Search across all columns…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="dv-search-clear" onClick={() => setSearch('')}>×</button>
          )}
        </div>

        <div className="dv-toolbar-right">
          {/* Column visibility toggle */}
          <div className="dv-col-menu-wrapper" ref={colMenuRef}>
            <button
              className="dv-col-toggle-btn"
              onClick={() => setColMenuOpen(v => !v)}
              title="Show/hide columns"
            >
              Columns ▾
            </button>
            {colMenuOpen && (
              <div className="dv-col-menu">
                {columns.map(col => (
                  <label key={col.key} className="dv-col-menu-item">
                    <input
                      type="checkbox"
                      checked={!hiddenColumns.has(col.key)}
                      onChange={() => toggleColumn(col.key)}
                    />
                    <span>{col.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="dv-row-count">
            {totalRows.toLocaleString()} row{totalRows !== 1 ? 's' : ''}
          </div>

          {onHelpSection && (
            <button
              className="help-info-icon dv-help-icon"
              onClick={() => onHelpSection('dataViewer')}
              title="Learn about Data Viewer"
            >
              ⓘ
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="dv-table-wrapper">
        {loading && rows.length === 0 ? (
          <div className="dv-loading">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="dv-empty">No data matches the current filters.</div>
        ) : (
          <table className="dv-table">
            <thead>
              <tr>
                <th className="dv-row-num-header">#</th>
                {visibleColumns.map(col => (
                  <th
                    key={col.key}
                    className={`dv-header-cell${sortColumn === col.key ? ' sorted' : ''}`}
                    onClick={() => handleSort(col.key)}
                    title={`Sort by ${col.label}`}
                  >
                    <span className="dv-header-label">{col.label}</span>
                    {sortColumn === col.key && (
                      <span className="dv-sort-arrow">
                        {sortDirection === 'asc' ? ' ▲' : ' ▼'}
                      </span>
                    )}
                  </th>
                ))}
                {onOpenDocument && <th className="dv-row-num-header" aria-label="Open"></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const fn = onOpenDocument ? getRowFilename(row) : null;
                return (
                <tr key={idx} className={idx % 2 === 0 ? 'dv-even' : 'dv-odd'}>
                  <td className="dv-row-num">{(page - 1) * pageSize + idx + 1}</td>
                  {visibleColumns.map(col => (
                    <td key={col.key} className="dv-cell" title={formatCell(row[col.key])}>
                      {formatCell(row[col.key])}
                    </td>
                  ))}
                  {onOpenDocument && (
                    <td className="dv-cell dv-open-cell">
                      {fn ? (
                        <button
                          className="dv-open-btn"
                          onClick={() => onOpenDocument(fn, getRowParaIdx(row))}
                          title="Open source document"
                        >
                          Open ↗
                        </button>
                      ) : null}
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {loading && rows.length > 0 && (
          <div className="dv-loading-overlay">Updating…</div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="dv-pagination">
          <button
            className="dv-page-btn"
            disabled={page <= 1}
            onClick={() => setPage(1)}
            title="First page"
          >
            ««
          </button>
          <button
            className="dv-page-btn"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            ‹ Prev
          </button>
          <span className="dv-page-info">
            Page {page} of {totalPages}
          </span>
          <button
            className="dv-page-btn"
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next ›
          </button>
          <button
            className="dv-page-btn"
            disabled={page >= totalPages}
            onClick={() => setPage(totalPages)}
            title="Last page"
          >
            »»
          </button>
          <select
            className="dv-page-size"
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
          >
            <option value={25}>25 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
            <option value={250}>250 / page</option>
          </select>
        </div>
      )}
    </div>
  );
};

export default DataViewerPanel;
